import React, { useState, useMemo } from "react";
import { useSelector } from "utils/redux";
import { Select } from "components/inputs";
import { useConstants } from "utils/hooks";
import Button from "components/Button/Button.tsx";
import Card from "components/Card/Card";
import { useForm, Controller } from "react-hook-form";
import { useGenerateOpsReportMutation } from "services/api";
import { KaboodleToaster } from "components/common";

import "./OpsReporting.scss";
import KaboodleModal from "components/Modals/KaboodleModal";
import { initiateDownload } from "components/common/DownloadLink.tsx";
import LoadingIcon from "components/Loading/LoadingIcon.tsx";
import MultiSelect from "components/inputs/Select/MultiSelect";
import { DatePicker, DATE_TO_STRING, STRING_TO_DATE } from "components/inputs";
import { FEATURE_FLAGS } from "constants/FeatureFlags";

// Import new components for Tasks report
import TasksReportFilters from "./filters/TasksReportFilters";
import { useTasksForm } from "./hooks/useTasksForm";

const MSA_SERVICE_TYPES = {
  MSA_111: "MSA-111",
  MSA: "MSA",
  NTMSA: "NTMSA",
  PRE_MSA_SNAPSHOT: "PRE-MSA",
  AMENDED_REVIEW: "ARV",
};

const REPORT_TYPES = {
  ALBERTSONS: "ALBERTSONS",
  LIENS: "LIENS",
  MSA: "MSA",
  TASKS: "TASKS",
  COST_PROJECTION: "COST_PROJECTION",
  LIBERTY_SUPPLIER_REPORT: "LIBERTY_SUPPLIER_REPORT",
};

export default function OpsReporting() {
  const accessToken = useSelector((state) => state.main.accessToken);
  const { constants } = useConstants();
  const [generatingModalIsOpen, setGeneratingModalIsOpen] = useState(false);

  // Use the existing tasks form hook for Tasks report
  const tasksForm = useTasksForm();

  // Single form for all other report types
  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    watch,
  } = useForm({
    defaultValues: {
      report_type: null,
      client_id: null,
      msa_service_types: null,
      years: null,
      start_date: null,
      end_date: null,
      include_app_url: false,
    },
  });

  const [generateOpsReport] = useGenerateOpsReportMutation();

  const watchedReportType = watch("report_type");
  const isTasksReport = watchedReportType?.value === REPORT_TYPES.TASKS;

  const reportTypeOptions = useMemo(() => {
    const isLocal = import.meta.env.VITE_APP_ENV === "local";
    const isLibertyEnabled =
      constants?.FEATURE_FLAGS?.[FEATURE_FLAGS.LIBERTY_SUPPLIER_REPORT_ENABLED];
    return constants?.OPS_REPORT_TYPES?.filter((rt) => {
      // Defense in depth: the constants endpoint already omits this option when
      // the flag is off, but gate it here too in case it is ever returned.
      if (rt.value === REPORT_TYPES.LIBERTY_SUPPLIER_REPORT) {
        return Boolean(isLibertyEnabled);
      }
      if (isLocal) {
        return true;
      }
      return [
        REPORT_TYPES.ALBERTSONS,
        REPORT_TYPES.LIENS,
        REPORT_TYPES.MSA,
        REPORT_TYPES.COST_PROJECTION,
        REPORT_TYPES.TASKS,
      ].includes(rt.value);
    });
  }, [constants]);

  const msaServiceTypes = [
    { label: "MSA-111", value: MSA_SERVICE_TYPES.MSA_111 },
    { label: "MSA", value: MSA_SERVICE_TYPES.MSA },
    { label: "NTMSA", value: MSA_SERVICE_TYPES.NTMSA },
    { label: "PRE-MSA", value: MSA_SERVICE_TYPES.PRE_MSA_SNAPSHOT },
    { label: "ARV", value: MSA_SERVICE_TYPES.AMENDED_REVIEW },
  ];

  const onSubmit = async (data) => {
    setGeneratingModalIsOpen(true);

    let requestData = {};

    if (isTasksReport) {
      // Use Tasks report data from the tasks form
      const tasksData = tasksForm.form.watch();
      requestData = {
        client_id: tasksData.client_id?.value,
        report_type: REPORT_TYPES.TASKS,
        assignee_ids:
          tasksData.assignee_ids?.map((assignee) => assignee.value) || [],
        status: tasksData.status?.value,
        service_type: tasksData.service_type?.value,
        created_after: tasksData.created_after,
        created_before: tasksData.created_before,
        started_after: tasksData.started_after,
        started_before: tasksData.started_before,
        completed_after: tasksData.completed_after,
        completed_before: tasksData.completed_before,
      };
    } else {
      // Use original data structure for other reports
      requestData = {
        years: data?.years?.map((y) => y.value) || [],
        msa_service_types: data?.msa_service_types?.map((m) => m.value) || [],
        include_app_url: data?.include_app_url,
        start_date: data?.start_date,
        end_date: data?.end_date,
        report_type: data?.report_type?.value,
        client_id: data?.client_id?.value,
      };
    }

    // Remove null/undefined values
    Object.keys(requestData).forEach((key) => {
      if (
        requestData[key] === null ||
        requestData[key] === undefined ||
        requestData[key] === ""
      ) {
        delete requestData[key];
      }
    });

    const response = await generateOpsReport({
      data: requestData,
    });

    if (
      response?.error &&
      "data" in response.error &&
      (response.error as { data?: { error?: string } }).data?.error
    ) {
      setGeneratingModalIsOpen(false);
      KaboodleToaster.error(
        (response.error as { data?: { error?: string } }).data.error as string,
      );
      throw new Error(
        `Error trying to generate report with response ${JSON.stringify(response?.error)}`,
      );
    } else if (
      (response as { data?: { url: string; filename: string } }).data?.url
    ) {
      initiateDownload(
        (response as { data?: { url: string; filename: string } }).data.url,
        accessToken,
        (response as { data?: { url: string; filename: string } }).data
          .filename,
      );
      setGeneratingModalIsOpen(false);
    } else {
      setGeneratingModalIsOpen(false);
      KaboodleToaster.error(`Failed to generate report.`);
      throw new Error(
        `Failed to generate report with response ${JSON.stringify(response)}`,
      );
    }
  };

  const generatingModal = useMemo(() => {
    if (!generatingModalIsOpen) {
      return null;
    }
    return (
      <KaboodleModal
        isOpen={generatingModalIsOpen}
        onClose={() => {
          setGeneratingModalIsOpen(false);
        }}
      >
        <LoadingIcon message="Generating Report" />
      </KaboodleModal>
    );
  }, [generatingModalIsOpen, setGeneratingModalIsOpen]);

  // Original conditional filter logic
  const SHOW_YEARS_FILTER = constants?.OPS_REPORT_TYPES
    ? [REPORT_TYPES.ALBERTSONS].includes(watchedReportType?.value)
    : false;

  const currentYear = new Date().getFullYear();
  const startYear = 2025;
  const YEAR_OPTIONS = Array.from(
    { length: currentYear - startYear + 1 },
    (_, index) => ({
      value: (startYear + index).toString(),
      label: (startYear + index).toString(),
    }),
  );

  const SHOW_MSA_TYPE_FILTER = constants?.OPS_REPORT_TYPES
    ? [REPORT_TYPES.ALBERTSONS, REPORT_TYPES.MSA].includes(
        watchedReportType?.value,
      )
    : false;

  const SHOW_START_END_DATES_FILTER = constants?.OPS_REPORT_TYPES
    ? [
        REPORT_TYPES.LIENS,
        REPORT_TYPES.MSA,
        REPORT_TYPES.COST_PROJECTION,
        REPORT_TYPES.LIBERTY_SUPPLIER_REPORT,
      ].includes(watchedReportType?.value)
    : false;

  // const handleClearFilters = () => {
  //   if (isTasksReport) {
  //     tasksForm.handleClearFilters();
  //   } else {
  //     reset({
  //       report_type: watchedReportType, // Keep the current report type
  //       client_id: null,
  //       msa_service_types: null,
  //       years: null,
  //       start_date: null,
  //       end_date: null,
  //       include_app_url: false,
  //     });
  //   }
  // };

  // Determine which form to use for submission
  const handleFormSubmit = isTasksReport
    ? tasksForm.form.handleSubmit(onSubmit)
    : handleSubmit(onSubmit);

  // Reflect the submitting state of whichever form actually handles the submit
  // so the button disables / shows "Generating..." for Tasks reports too (their
  // submit goes through the tasks form, not the main form's `isSubmitting`).
  const isReportSubmitting = isTasksReport
    ? tasksForm.form.isSubmitting
    : isSubmitting;

  return (
    <div className="page-container">
      <div className="page-content">
        <div className="OpsReporting">
          <Card heading="Operations Reporting">
            <form onSubmit={handleFormSubmit}>
              {generatingModal}

              {/* Report Type - Always shown on its own line */}
              <Controller
                name="report_type"
                control={control}
                rules={{ required: "Report Type is required" }}
                render={({ field: { name, onChange, value } }) => (
                  <Select
                    name={name}
                    label="Report Type"
                    options={reportTypeOptions}
                    errors={errors}
                    value={value}
                    onChange={onChange}
                    required={true}
                  />
                )}
              />

              {/* Tasks Report Filters */}
              {isTasksReport && (
                <TasksReportFilters
                  key={tasksForm.formKey}
                  control={tasksForm.form.control}
                  errors={tasksForm.form.errors}
                  watchedStartDate={tasksForm.form.watch("created_after")}
                  watchedEndDate={tasksForm.form.watch("created_before")}
                  watchedReportType={watchedReportType}
                  clientOptions={tasksForm.options.clientOptions}
                  assigneeOptions={tasksForm.options.assigneeOptions}
                  statusOptions={tasksForm.options.statusOptions}
                  serviceTypeOptions={constants?.SERVICE_TYPE || []}
                  isClientsFetching={tasksForm.loading.isClientsFetching}
                  isAssigneesFetching={tasksForm.loading.isAssigneesFetching}
                  disabled={false}
                  debouncedSetClientSearchTerm={
                    tasksForm.search.debouncedSetClientSearchTerm
                  }
                  debouncedSetAssigneeSearchTerm={
                    tasksForm.search.debouncedSetAssigneeSearchTerm
                  }
                />
              )}

              {/* Other Report Types Filters */}
              {!isTasksReport && watchedReportType && (
                <div className="grouped-query-section">
                  {/* Client Name - Required for non-Tasks reports */}
                  <Controller
                    name="client_id"
                    control={control}
                    rules={{ required: "Client is required" }}
                    render={({ field: { name, onChange, value } }) => (
                      <Select
                        name={name}
                        label="Client Name"
                        options={tasksForm.options.clientOptions}
                        errors={errors}
                        value={value}
                        placeholder="Search..."
                        noOptionsMessage={({ inputValue }) =>
                          !inputValue
                            ? "Begin typing to search for clients..."
                            : tasksForm.loading.isClientsFetching
                              ? "Searching..."
                              : "No clients found."
                        }
                        onInputChange={(inputValue: string) =>
                          tasksForm.search.debouncedSetClientSearchTerm(
                            inputValue,
                          )
                        }
                        onChange={onChange}
                        required={true}
                      />
                    )}
                  />

                  {/* Years filter */}
                  {SHOW_YEARS_FILTER && (
                    <Controller
                      name="years"
                      control={control}
                      render={({ field: { name, onChange, value } }) => (
                        <MultiSelect
                          name={name}
                          label="Years"
                          required={true}
                          options={YEAR_OPTIONS}
                          value={value}
                          errors={errors}
                          onChange={onChange}
                          placeholder="Select years..."
                        />
                      )}
                    />
                  )}

                  {/* MSA Types filter */}
                  {SHOW_MSA_TYPE_FILTER && (
                    <Controller
                      name="msa_service_types"
                      control={control}
                      render={({ field: { name, onChange, value } }) => (
                        <MultiSelect
                          name={name}
                          label="MSA Types"
                          required={true}
                          widthVariant="wide"
                          options={msaServiceTypes}
                          value={value}
                          errors={errors}
                          onChange={onChange}
                          placeholder="Select MSA Types..."
                        />
                      )}
                    />
                  )}

                  {/* Start/End Date filters */}
                  {SHOW_START_END_DATES_FILTER && (
                    <div className="filters-row">
                      <Controller
                        name="start_date"
                        control={control}
                        rules={{ required: "Start Date is required" }}
                        render={({ field: { value, name, onChange } }) => (
                          <DatePicker
                            name={name}
                            initialDate={STRING_TO_DATE(value)}
                            label="Start Date"
                            required={true}
                            onDateChange={(date) =>
                              onChange(DATE_TO_STRING(date))
                            }
                            errors={errors}
                          />
                        )}
                      />
                      <Controller
                        name="end_date"
                        control={control}
                        rules={{ required: "End Date is required" }}
                        render={({ field: { value, name, onChange } }) => (
                          <DatePicker
                            name={name}
                            label="End Date"
                            required={true}
                            initialDate={STRING_TO_DATE(value)}
                            onDateChange={(date) =>
                              onChange(DATE_TO_STRING(date))
                            }
                            errors={errors}
                          />
                        )}
                      />
                    </div>
                  )}
                </div>
              )}

              {/* Report actions */}
              <div className="button-groups-container">
                <div className="button-row">
                  {/* <Button
                    onClick={handleClearFilters}
                    className="report-button"
                  >
                    Clear Filters
                  </Button> */}
                  {/* Submission is handled by the form's onSubmit
                      (handleFormSubmit), which dispatches to the tasks or main
                      form. The button is type="submit" only -- no onClick -- so
                      a click fires exactly one handler (not two). */}
                  <Button
                    type="submit"
                    className="report-button"
                    disabled={isReportSubmitting}
                  >
                    {isReportSubmitting ? "Generating..." : "Generate Report"}
                  </Button>
                </div>
              </div>
            </form>
          </Card>
        </div>
      </div>
    </div>
  );
}
